import {
  Carriers,
  Service,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  Carrier,
  CarrierLabelDateRestrictions,
  carrierToLabel,
  exceedsCarrierLabelDateRestriction,
  isUpsCarrier,
  isUpsService,
  isUspsCarrier,
  LabelCustomLineNumbers,
  upsInternationalServices,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  FulfillmentGroupData,
  shouldUseVerifiedDestinationAddress,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { groupFulfillmentOrderLineItems } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import {
  distributeAcrossLines,
  MIN_PROVIDER_REFERENCE_LINE_LENGTH,
} from "@redotech/redo-fulfillment-rate-common/label-skus";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import {
  buildSkuList,
  isPoBoxAddress,
} from "@redotech/redo-fulfillment-rate-common/outbound-shipment";
import { customsRequired } from "../shipping/util";
import { Team } from "../team";

export interface RateForFlag {
  carrier: Carrier;
  service: Service;
  provider?: RateProvider;
}

export interface ShipmentFlag {
  message: string;
  shouldDisplay: ({
    selectedRate,
    fulfillmentGroup,
    team,
    fulfillmentDate,
  }: {
    selectedRate?: RateForFlag | null;
    fulfillmentGroup: FulfillmentGroupData;
    team?: Team | null;
    fulfillmentDate: Date | null;
  }) => boolean;
}

function calculateMinLinesNeededForSkus(
  fulfillmentGroup: FulfillmentGroupData,
): number {
  const groupedLineItems = groupFulfillmentOrderLineItems(
    fulfillmentGroup.items,
  );
  const skus = groupedLineItems.map((item) => ({
    sku: item.sku ?? "",
    quantity: item.quantity,
  }));
  const skuList = buildSkuList(skus);
  const distributedByLines = distributeAcrossLines(
    skuList,
    MIN_PROVIDER_REFERENCE_LINE_LENGTH,
  );
  return distributedByLines.length;
}

export enum ShipmentFlagType {
  UPS_DESTINATION_ADDRESS_LINE1_LENGTH = "UPS_DESTINATION_ADDRESS_LINE1_LENGTH",
  UPS_DESTINATION_ADDRESS_LINE2_LENGTH = "UPS_DESTINATION_ADDRESS_LINE2_LENGTH",
  USPS_DESTINATION_ADDRESS_LINE1_LENGTH = "USPS_DESTINATION_ADDRESS_LINE1_LENGTH",
  USPS_DESTINATION_ADDRESS_LINE2_LENGTH = "USPS_DESTINATION_ADDRESS_LINE2_LENGTH",
  USPS_PO_BOX_SIZE_WARNING = "USPS_PO_BOX_SIZE_WARNING",
  SKU_TRUNCATION_WARNING = "SKU_TRUNCATION_WARNING",
  UPS_CONTENTS_EXPLANATION_REQUIRED = "UPS_CONTENTS_EXPLANATION_REQUIRED",
  DESTINATION_EMAIL_LENGTH = "DESTINATION_EMAIL_LENGTH",
  CUSTOMS_REQUIRED_NO_ITEMS = "CUSTOMS_REQUIRED_NO_ITEMS",
  CUSTOMS_ITEMS_MISSING_HS_CODES = "CUSTOMS_ITEMS_MISSING_HS_CODES",
  CARRIER_LABEL_DATE_RESTRICTIONS = "CARRIER_LABEL_DATE_RESTRICTIONS",
}

export const UPSDestinationAddressLine1Length: ShipmentFlag = {
  message:
    "Address line 1 may need to be fewer than 35 alphanumeric characters",
  shouldDisplay: ({ selectedRate, fulfillmentGroup, team }) => {
    if (!selectedRate || !isUpsCarrier(selectedRate.carrier)) {
      return false;
    }

    const useVerified = shouldUseVerifiedDestinationAddress(
      fulfillmentGroup,
      team?.settings?.outboundLabels?.address?.autoCleanAddresses,
    );

    const addressToCheck = useVerified
      ? fulfillmentGroup?.verifiedDestinationAddress ||
        fulfillmentGroup?.destinationAddress
      : fulfillmentGroup?.destinationAddress;

    return (
      !!addressToCheck?.address1?.length && addressToCheck.address1.length > 35
    );
  },
};

export const UPSDestinationAddressLine2Length: ShipmentFlag = {
  message:
    "Address line 2 may need to be fewer than 35 alphanumeric characters",
  shouldDisplay: ({ selectedRate, fulfillmentGroup, team }) => {
    if (!selectedRate || !isUpsCarrier(selectedRate.carrier)) {
      return false;
    }

    const useVerified = shouldUseVerifiedDestinationAddress(
      fulfillmentGroup,
      team?.settings?.outboundLabels?.address?.autoCleanAddresses,
    );

    const addressToCheck = useVerified
      ? fulfillmentGroup?.verifiedDestinationAddress ||
        fulfillmentGroup?.destinationAddress
      : fulfillmentGroup?.destinationAddress;

    return (
      !!addressToCheck?.address2?.length && addressToCheck.address2.length > 35
    );
  },
};

export const USPSDestinationAddressLine1Length: ShipmentFlag = {
  message:
    "Address line 1 is longer than 35 characters and may be cut off on the USPS label",
  shouldDisplay: ({ selectedRate, fulfillmentGroup, team }) => {
    if (!selectedRate || !isUspsCarrier(selectedRate.carrier)) {
      return false;
    }

    const useVerified = shouldUseVerifiedDestinationAddress(
      fulfillmentGroup,
      team?.settings?.outboundLabels?.address?.autoCleanAddresses,
    );

    const addressToCheck = useVerified
      ? fulfillmentGroup?.verifiedDestinationAddress ||
        fulfillmentGroup?.destinationAddress
      : fulfillmentGroup?.destinationAddress;

    return (
      !!addressToCheck?.address1?.length && addressToCheck.address1.length > 35
    );
  },
};

export const USPSDestinationAddressLine2Length: ShipmentFlag = {
  message:
    "Address line 2 is longer than 35 characters and may be cut off on the USPS label",
  shouldDisplay: ({ selectedRate, fulfillmentGroup, team }) => {
    if (!selectedRate || !isUspsCarrier(selectedRate.carrier)) {
      return false;
    }

    const useVerified = shouldUseVerifiedDestinationAddress(
      fulfillmentGroup,
      team?.settings?.outboundLabels?.address?.autoCleanAddresses,
    );

    const addressToCheck = useVerified
      ? fulfillmentGroup?.verifiedDestinationAddress ||
        fulfillmentGroup?.destinationAddress
      : fulfillmentGroup?.destinationAddress;

    return (
      !!addressToCheck?.address2?.length && addressToCheck.address2.length > 35
    );
  },
};

export const SkuTruncationWarning: ShipmentFlag = {
  message: "Some SKUs may be truncated on the shipping label",
  shouldDisplay: ({ selectedRate, fulfillmentGroup, team }) => {
    if (!selectedRate) {
      return false;
    }

    if (
      !team?.settings?.outboundLabels?.documents?.shippingLabel?.includeSkus
    ) {
      return false;
    }

    const minLinesNeeded = calculateMinLinesNeededForSkus(fulfillmentGroup);
    const availableLines = LabelCustomLineNumbers[selectedRate.carrier];

    return minLinesNeeded > availableLines;
  },
};

export const USPSPosBoxSizeWarning: ShipmentFlag = {
  message: "Package dimensions may be too large to deliver to a PO Box",
  shouldDisplay: ({ fulfillmentGroup }) => {
    const isPoBox = isPoBoxAddress({
      line1: fulfillmentGroup.destinationAddress.address1,
      line2: fulfillmentGroup.destinationAddress.address2,
    });

    const missingUSPSRates =
      fulfillmentGroup.availableShipmentRates &&
      !fulfillmentGroup.availableShipmentRates.rates.some((rate) =>
        isUspsCarrier(rate.carrier),
      );

    const hasNoRates =
      !fulfillmentGroup.availableShipmentRates ||
      fulfillmentGroup.availableShipmentRates.rates.length === 0;

    return isPoBox && !!(missingUSPSRates || hasNoRates);
  },
};

export const UpsContentsExplanationRequired: ShipmentFlag = {
  message:
    "Contents explanation required - item descriptions will be used as a fallback",
  shouldDisplay: ({ selectedRate, fulfillmentGroup, team }) => {
    if (!selectedRate) {
      return false;
    }

    const isUpsInternational =
      selectedRate.provider === RateProvider.UPS &&
      isUpsService(selectedRate.service) &&
      upsInternationalServices.includes(selectedRate.service);

    if (!isUpsInternational) {
      return false;
    }

    const hasGroupShipmentDescription = !!fulfillmentGroup.contentsExplanation;

    const teamHasDefaultShipmentDescription =
      !!team?.settings?.outboundLabels?.customs?.defaultContentsDescription;

    return !hasGroupShipmentDescription && !teamHasDefaultShipmentDescription;
  },
};

export const DestinationEmailLength: ShipmentFlag = {
  message:
    "Destination email address is longer than 50 characters, which may impact the ability to purchase labels",
  shouldDisplay: ({ fulfillmentGroup }) => {
    const email = fulfillmentGroup?.destinationAddress?.email;
    return !!email && email.length > 50;
  },
};

export const CustomsRequiredNoItems: ShipmentFlag = {
  message:
    "Customs information is required for this shipment but no customs items have been added",
  shouldDisplay: ({ fulfillmentGroup }) => {
    const needsCustoms = customsRequired({
      toAddress: fulfillmentGroup.destinationAddress,
      fromAddress: fulfillmentGroup.originAddress,
    });

    if (!needsCustoms) {
      return false;
    }

    const hasCustomsDeclarations =
      (fulfillmentGroup.customs?.declarations?.length ?? 0) > 0;

    return !hasCustomsDeclarations;
  },
};

export const CustomsItemsMissingHsCodes: ShipmentFlag = {
  message: "Some customs items are missing harmonization codes (HS codes)",
  shouldDisplay: ({ fulfillmentGroup }) => {
    const needsCustoms = customsRequired({
      toAddress: fulfillmentGroup.destinationAddress,
      fromAddress: fulfillmentGroup.originAddress,
    });

    if (!needsCustoms) {
      return false;
    }

    const allDeclarations = fulfillmentGroup.customs?.declarations ?? [];

    if (allDeclarations.length === 0) {
      return false;
    }

    return allDeclarations.some(
      (declaration) => !declaration.harmonizationCode,
    );
  },
};

export const CarrierLabelDateRestrictionWarning: ShipmentFlag = {
  message: `The selected fulfillment date exceeds the carrier label date restrictions. The date will be clamped to the maximum allowed date. Maximum label dates by carrier are: ${Object.entries(
    CarrierLabelDateRestrictions,
  )
    .filter(
      ([carrier, maxDays]) =>
        !!maxDays &&
        carrier !== Carriers.UPS_SUREPOST &&
        carrier !== Carriers.FEDEX_SMART_POST &&
        carrier !== Carriers.USPS_SHIP,
    )
    .map(
      ([carrier, maxDays]) =>
        `${carrierToLabel[carrier as Carriers]}: ${maxDays} days`,
    )
    .join(", ")}`,
  shouldDisplay: ({ selectedRate, fulfillmentDate }) => {
    if (!selectedRate || !fulfillmentDate) {
      return false;
    }

    return exceedsCarrierLabelDateRestriction(
      fulfillmentDate,
      selectedRate.carrier,
    );
  },
};

const allShipmentFlags: Record<ShipmentFlagType, ShipmentFlag> = {
  [ShipmentFlagType.UPS_DESTINATION_ADDRESS_LINE1_LENGTH]:
    UPSDestinationAddressLine1Length,
  [ShipmentFlagType.UPS_DESTINATION_ADDRESS_LINE2_LENGTH]:
    UPSDestinationAddressLine2Length,
  [ShipmentFlagType.USPS_DESTINATION_ADDRESS_LINE1_LENGTH]:
    USPSDestinationAddressLine1Length,
  [ShipmentFlagType.USPS_DESTINATION_ADDRESS_LINE2_LENGTH]:
    USPSDestinationAddressLine2Length,
  [ShipmentFlagType.SKU_TRUNCATION_WARNING]: SkuTruncationWarning,
  [ShipmentFlagType.USPS_PO_BOX_SIZE_WARNING]: USPSPosBoxSizeWarning,
  [ShipmentFlagType.UPS_CONTENTS_EXPLANATION_REQUIRED]:
    UpsContentsExplanationRequired,
  [ShipmentFlagType.DESTINATION_EMAIL_LENGTH]: DestinationEmailLength,
  [ShipmentFlagType.CUSTOMS_REQUIRED_NO_ITEMS]: CustomsRequiredNoItems,
  [ShipmentFlagType.CUSTOMS_ITEMS_MISSING_HS_CODES]: CustomsItemsMissingHsCodes,
  [ShipmentFlagType.CARRIER_LABEL_DATE_RESTRICTIONS]:
    CarrierLabelDateRestrictionWarning,
};

export function getAllShipmentFlags(): ShipmentFlag[] {
  return Object.values(allShipmentFlags);
}
